<template>
  <div :class="['UIAlert', `UIAlert__${position}`]">
    <TransitionGroup name="UIAlert__item">
      <div
        v-for="alert in alertStore.alerts"
        :key="alert.id"
        :class="['UIAlert__item', `UIAlert__item-${alert.type}`]"
      >
        <div class="UIAlert__item-content">
          <UIIcon class="UIAlert__item-icon" :name="iconMap[alert.type]" />
          <div class="UIAlert__item-text">
            <h5 v-if="alert.title" class="UIAlert__item-title">
              {{ alert.title }}
            </h5>
            <div
              class="UIAlert__item-message"
              v-html="DOMPurify.sanitize(alert.message)"
            />

            <div v-if="alert.actions" class="UIAlert__item-actions">
              <UIButton
                v-for="([label, action], index) in Object.entries(
                  alert.actions,
                )"
                :key="`${label}_${index}`"
                :theme="'neutral'"
                :buttonStyle="'text'"
                @click="action()"
              >
                {{ label }}
              </UIButton>
            </div>
          </div>

          <UIButtonIcon
            icon="close"
            class="UIAlert__close-icon"
            @click="() => alertStore.removeAlert(alert.id)"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script lang="ts" setup>
import DOMPurify from 'dompurify'

import { useAlert } from '@/store/alert'
import UIButtonIcon from '@/views/components/ui/buttons/UIButtonIcon.vue'

import { ALERT_POSITION } from './types'
import UIButton from '../buttons/UIButton.vue'
import UIIcon from '../icons/UIIcon.vue'

import type { ALERT_TYPE } from './types'
import type { IIcons } from '../icons/types'

const iconMap: Record<ALERT_TYPE, IIcons> = {
  success: 'checkCircle',
  error: 'exclamationTriangleOutline',
  warning: 'exclamationCircleOutline',
  caution: 'exclamationCircleOutline',
  info: 'exclamationCircleOutline',
  basic: 'accountOutline',
}
const alertStore = useAlert()

withDefaults(
  defineProps<{
    position?: ALERT_POSITION
  }>(),
  {
    position: ALERT_POSITION.BOTTOM_RIGHT,
  },
)
</script>

<style lang="scss" scoped>
.UIAlert {
  z-index: 1998;
  position: fixed;
  padding: 1rem;
  max-width: 27rem;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  &__item {
    position: relative;
    pointer-events: auto;
    background: $color-white;
    box-shadow: 0px 5px 10px -2px #09092d26;
    border-radius: var(--radius-s);
    opacity: 1;
    padding: 1rem;
    width: 100%;
    max-width: 100%;
    line-height: 1.3rem;

    &:not(:last-child) {
      margin-bottom: 1rem;
    }

    &-icon {
      --size: 1.5rem;
      --color: var(--alert-color, $color-neutral-170);
      margin-right: 0.5rem;
    }

    &-content {
      @include font-size-16px;
      color: var(--alert-color, $color-neutral-170);
      display: flex;
      align-items: flex-start;
      width: 100%;
      min-height: 2.25rem;

      .UIIcon {
        --size: 1.5rem;
      }
      .UIButtonIcon {
        --icon-size: 1.25rem;
        margin-left: 0.25rem;
      }
    }

    &-title {
      margin-bottom: 0.35rem;
      font-size: 1rem;
    }

    &-text {
      width: 100%;
      font-weight: 500;

      :deep(a) {
        color: $color-neutral-170;
        text-decoration: none;
      }
    }

    @mixin alertTheme($color) {
      --alert-color: #{$color};
    }

    &-success {
      @include alertTheme($color-success-60);
    }
    &-error {
      @include alertTheme($color-error-60);
    }
    &-warning {
      @include alertTheme($color-warning-60);
    }
    &-caution {
      @include alertTheme($color-caution-60);
    }
    &-info {
      @include alertTheme($color-info-60);
    }
    &-basic {
      @include alertTheme($color-neutral-140);
    }

    // UIAlert item animation
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.5s ease;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateX(1.5rem);
    }
  }

  &__top-right {
    top: 0rem;
    right: 0rem;
  }

  &__top-left {
    top: 0rem;
    left: 0rem;
  }

  &__top-center {
    top: 0rem;
    left: 0;
    right: 0;
    margin: 0 auto;
  }

  &__bottom-left {
    bottom: 0rem;
    left: 0rem;
  }

  &__bottom-right {
    bottom: 0rem;
    right: 0rem;
  }

  .UIAlert__close-icon {
    --icon-size: 1rem;
    --icon-color: #{$color-neutral-140};
  }

  .UIAlert__item-title {
    margin-bottom: 1rem;
  }

  .UIAlert__item-message {
    @include font-size-14px;
    color: $color-neutral-170;
  }

  .UIAlert__item-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}
</style>
